'use strict';

import EventEmitter from 'common/event-emitter';
import index from '../index';
import * as Constants from './Constants';

// If width falls below this number we are assuming we want the fullscreen.
const FullscreenThreshold = 500;

/**
 * Instance of a web messenger.
 * Encapsulates the iframe containing the web messenger
 */
export default class FxoMessenger extends EventEmitter {
  constructor(data = {}, containerElement = null) {
    super();
    this.config = data;

    this.state = 'loading';

    this._eventHandler = this._processPostMessageCommands.bind(this);

    //If a container element is present, we'll ignore popup height because
    //the messenger is not being hosted as a popup, but inline
    if (
      this.config.popupHeight &&
      this.config.popupHeight.trim() !== '' &&
      !containerElement
    ) {
      this.config.mode = 'popup';
    }

    this._addMessenger(containerElement);
    this._addPostMessageListener();
  }

  destroy() {
    this.removeListeners();
    this._removePostMessageListener();
    this._removeMessenger();
  }

  setConfig(config) {
    this.config = config;
    this._sendPostMessageCommand(Constants.MESSAGES.CONFIG, { config });
    this._updateViewMode();
  }

  getState() {
    const {
      color,
      fullscreen,
      headerText,
      botIcon,
      disableAttachments,
      enableRecording,
      autoplayAudio,
      collectUserIpAddress,
      testConsole,
    } = this.config;
    return {
      state: this.state,
      color,
      fullscreen,
      headerText,
      botIcon,
      testConsole,
      disableAttachments,
      enableRecording,
      autoplayAudio,
      collectUserIpAddress,
    };
  }

  sendMessage(text, metadata) {
    this._sendPostMessageCommand(Constants.MESSAGES.SEND_MESSAGE, {
      text,
      metadata,
    });
  }

  get state() {
    return this._state;
  }

  set state(newState) {
    this._state = newState;
  }

  // 'private' methods

  _sendPostMessageCommand(type, data) {
    const command = Object.assign({ type: type }, data);
    this._iframe.contentWindow.postMessage(command, '*');
  }

  // Process any commands received from the messenger iframe
  _processPostMessageCommands(payload) {
    const message = payload.data;
    switch (message.type) {
      // Internal messages
      case Constants.MESSAGES.REQUEST_CONFIG:
        this.setConfig(this.config);
        break;
      case Constants.MESSAGES.CLOSE:
        this.emit(Constants.EVENTS.CLOSED);
        this.destroy();
        break;

      // Externally usable events
      case Constants.MESSAGES.STATE_UPDATE:
        this.state = message.state;
        this.emit(Constants.EVENTS.STATE_CHANGED, this.state);
        break;
      case Constants.MESSAGES.MESSAGE_RECEIVED:
        this.emit(Constants.EVENTS.MESSAGE_RECEIVED, message.message);
        break;
      case Constants.MESSAGES.MESSAGE_SENT:
        this.emit(Constants.EVENTS.MESSAGE_SENT, message.message);
        break;
      case Constants.MESSAGES.CUSTOM_RECEIVED:
        this.emit(Constants.EVENTS.CUSTOM_RECEIVED, message.custom);
        if (
          message.custom &&
          message.custom.body &&
          message.custom.body.eval &&
          message.custom.body.eval.length > 0
        ) {
          try {
            eval(message.custom.body.eval);
          } catch (ex) {
            console.log(
              'Could not eval custom javascript (' +
                message.custom.body.eval +
                ') because of an error:' +
                ex
            );
          }
        }
        break;
      case Constants.MESSAGES.CONFIG_UPDATED:
        this.emit(Constants.EVENTS.CONFIG_UPDATED);
        break;
      case Constants.MESSAGES.FIRST_TIME_USER:
        this.emit(Constants.EVENTS.FIRST_TIME_USER);
        break;
    }
  }

  _addMessenger(containerElement = null) {
    const position = this.config.widgetPosition || 'right';
    const opPosition = position === 'right' ? 'left' : 'right';

    this._usesExternalContainer = !!containerElement;
    this._addStylesheet();
    this._calcIsFullscreen();
    // To get around what appears to be a rendering big in Chrome 66, we need to surround
    // the iFrame in a container and apply the box shadow to that rather than to the
    // iFrame itself.
    let { width, height } = this._getWidthAndHeightForMode();
    let containerSize = this._getWidthAndHeightForContainer();

    // Create iframe and add it to the host.
    const iFrameContainer = containerElement || document.createElement('div');
    const iframe = document.createElement('iframe');
    this._element = iframe;
    if (!this._usesExternalContainer) {
      Object.assign(iFrameContainer, {
        id: ' fxo-messenger-iframe-container',
        className: 'fxo-messenger-iframe-container',
      });
    }
    Object.assign(iframe, {
      id: 'fxo-messenger-iframe',
      className: 'fxo-messenger-iframe',
      scrolling: 'no',
      width: width,
      height: height,
      srcdoc: index(
        this.config.themeCssUrl,
        this.config.customCssUrl,
        this.config.themeName
      ),
      allowFullscreen: true,
      allowTransparency: true,
    });
    Object.assign(iframe.style, {
      'border': 'none',
      'position': 'fixed',
      'bottom': 0,
      [position]: 0,
      'z-index': 9998,
    });

    if (!this.config.fullscreen && !this._usesExternalContainer) {
      Object.assign(iFrameContainer.style, {
        'width': containerSize.width,
        'height': containerSize.height,
        'position': 'fixed',
        [position]: '0px',
        'bottom': '0px',
        'z-index': '9998',
        'box-shadow':
          '0 1px 6px rgba(17, 54, 84, .04), 0 2px 32px rgba(17, 54, 84, .08)',
      });

      Object.assign(iframe.style, {
        [`margin-${opPosition}`]: '20px',
      });
    }
    iFrameContainer.appendChild(this._iframe);
    try {
      if (this.config.fullscreen) {
        document.body.classList.add('fxo-messenger-fullscreen');
      }
      if (!this.config.allowBackgroundScroll) {
        document.body.classList.add('fxo-messenger-noscroll');
      }
    } catch (error) {
      console.error('Error adding class to parent body element.', error);
    }

    this._containerElement = iFrameContainer;
    if (!this._usesExternalContainer) {
      document.body.appendChild(this._container);
    }
  }

  _getWidthAndHeightForMode() {
    let width;
    let height = '100%';
    switch (this.config.mode) {
      case Constants.MODES.FULLSCREEN:
        width = '100%';
        break;
      case Constants.MODES.POPUP:
        width = this.config.popupWidth || 400;
        height = this.config.popupHeight || 400;
        break;
      default:
        width = this.config.fullscreen ? '100%' : this.config.popupWidth || 400;
        break;
    }
    return { width, height };
  }

  _getWidthAndHeightForContainer() {
    let width;
    let height = '100%';
    switch (this.config.mode) {
      case Constants.MODES.FULLSCREEN:
        width = '100%';
        break;
      case Constants.MODES.POPUP:
        width = this.config.popupWidth || '400px';
        height = this.config.popupHeight || '400px';
        break;
      default:
        width = this.config.fullscreen
          ? '100%'
          : this.config.popupWidth || '400px';
        break;
    }
    return { width, height };
  }

  _updateViewMode() {
    Object.assign(this._element, this._getWidthAndHeightForMode());
    this._sendPostMessageCommand(Constants.MESSAGES.VIEW_MODE_CHANGED);
  }

  _clearHistory() {
    this._sendPostMessageCommand(Constants.MESSAGES.CLEAR_HISTORY);
  }

  _forget() {
    this._sendPostMessageCommand(Constants.MESSAGES.FORGET_USER);
  }

  get _container() {
    return this._containerElement;
  }

  get _iframe() {
    return this._element;
  }

  _removeMessenger() {
    this._sendPostMessageCommand(Constants.MESSAGES.DESTROY);
    if (!this._usesExternalContainer) {
      document.body.removeChild(this._container);
    } else {
      this._iframe.remove();
    }
    this._removeStylesheet();
  }

  _addPostMessageListener() {
    window.addEventListener('message', this._eventHandler);
  }

  _removePostMessageListener() {
    window.removeEventListener('message', this._eventHandler);
  }

  _addStylesheet() {
    // Create stylesheet and add it to host
    const style = document.createElement('style');
    this._stylesheet = style;
    Object.assign(style, {
      id: 'fxo-messenger-styles',
      type: 'text/css',
    });

    document.body.classList.add('fxo-messenger-open');
    style.appendChild(
      document.createTextNode(
        '.fxo-messenger-fullscreen {overflow:hidden;height:100%;width:100%;position:fixed} .fxo-messenger-noscroll {overflow:hidden!important}'
      )
    );
    document.head.appendChild(style);
  }

  _removeStylesheet() {
    try {
      document.body.classList.remove('fxo-messenger-fullscreen');
      document.body.classList.remove('fxo-messenger-noscroll');
      document.body.classList.remove('fxo-messenger-open');
    } catch (error) {
      console.error('Error removing class from parent body element.', error);
    }
    document.head.removeChild(this._stylesheet);
  }

  _calcIsFullscreen() {
    this.config.fullscreen = window.innerWidth < FullscreenThreshold;
  }
}



// WEBPACK FOOTER //
// ./src/messenger/lib/FxoMessenger.js